import React from 'react';
import { Modal } from 'react-bootstrap';
import { 
  X, 
  Home, 
  Building2, 
  FileText, 
  Layers, 
  Check,   
  Zap,
  Calendar,
  Phone,
  DollarSign
} from 'lucide-react';

function ReconcileOverPaymentModal({
  showModalPayment,
  selectedRecord,
  formDataPayment,
  amountError,
  landlordApartment = [],
  apartmentHouse = [],
  selectedApartmentId,
  houseSelectedId,
  selectedCategory,
  selectedUtilities = [],
  selectedUtility,
  invoices = [],
  selectedInvoices = [],
  handleCloseModalPayment,
  handleSavePayment,
  handleChangePayment,
  handleApartmentSelect,
  handleHouseSelect,
  handleCategorySelect,
  setSelectedUtility,
  handleInvoiceToggle,
  calculateTotalInvoiceAmount
}) {
  // Helper booleans
  const isRentSelected = selectedCategory.includes('rent');
  const isUtilitySelected = selectedCategory.includes('expense');

  // Helper for button styles
  const getButtonClasses = (isActive) => {
    const baseClasses = "flex-1 flex items-center justify-center px-4 py-3 rounded-lg border transition-all duration-200 transform select-none shadow-sm";
    if (!houseSelectedId) {
      return `${baseClasses} bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-60`;
    }
    return `${baseClasses} cursor-pointer active:scale-95 ${
      isActive
        ? "bg-blue-600 border-blue-600 text-white shadow-blue-200"
        : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300"
    }`;
  };

  return (
    <Modal 
      show={showModalPayment} 
      onHide={handleCloseModalPayment} 
      size="lg" 
      centered
    >
     
      <div className="relative p-0 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-white to-gray-50">
        
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6" />
              <h3 className="text-xl font-bold">Reconcile Payment</h3>
            </div>
            <button
              onClick={handleCloseModalPayment}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20"></div>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[calc(100vh-150px)] overflow-y-auto custom-scrollbar">
          
          {/* --- Hero  --- */}
          {selectedRecord && (
            <div className="mb-6 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <div className="flex flex-wrap items-start justify-between gap-4">
                
                {/* Left Side: Overflow Amount */}
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold">
                    Overflow Amount
                  </p>
                  <p className="font-bold text-red-600 text-xl mt-0.5">
                    KES {parseFloat(selectedRecord.overflow_amount).toLocaleString('en-US')}
                  </p>
                </div>

                {/* Right Side: Details */}
                <div className="text-xs text-gray-700 space-y-1 text-left sm:text-right">
                  <p>
                    <span className="text-gray-500 font-medium">Paid By: </span>
                    <span className="font-semibold text-gray-900">{selectedRecord.first_name}</span>
                  </p>
                  <p>
                    <span className="text-gray-500 font-medium">Phone: </span>
                    <span className="font-semibold text-gray-900">{selectedRecord.phone_number}</span>
                  </p>
                  <p>
                    <span className="text-gray-500 font-medium">Trans ID: </span>
                    <span className="font-semibold text-gray-900">{selectedRecord.transcation_id}</span>
                  </p>
                  <p>
                    <span className="text-gray-500 font-medium">Unit: </span>
                    <span className="font-semibold text-gray-900">{selectedRecord.bill_ref_number || '-'}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSavePayment} className="space-y-5">
            
            {/* Property & House */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="flex items-center text-sm font-medium text-gray-700">
                  <Building2 className="w-4 h-4 mr-2 text-indigo-600" /> Property
                </p>
                <select
                  value={selectedApartmentId}
                  onChange={(e) => handleApartmentSelect(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-700"
                  required
                >
                  <option value="">Select Property</option>
                  {landlordApartment.map((landlord) => (
                    <option key={landlord.id} value={landlord.id}>{landlord.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <p className="flex items-center text-sm font-medium text-gray-700">
                  <Home className="w-4 h-4 mr-2 text-indigo-600" /> House
                </p>
                <select
                  value={houseSelectedId}
                  onChange={(e) => handleHouseSelect(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-700"
                  required
                  disabled={!selectedApartmentId}
                >
                  <option value="">Select House</option>
                  {apartmentHouse.map((house) => (
                    <option key={house.house_id} value={house.house_id}>
                      {house.house} - {house.tenant}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category Selection */}
            <div className="space-y-2">
              <p className="flex items-center text-sm font-medium text-gray-700">
                <Layers className="w-4 h-4 mr-2 text-indigo-600" /> Category (Multiple)
              </p>
              <div className="flex space-x-3">
                <div 
                  onClick={() => houseSelectedId && handleCategorySelect('rent', !isRentSelected)}
                  className={getButtonClasses(isRentSelected)}
                >
                  <div className="flex items-center space-x-2">
                    {isRentSelected ? <Check className="w-4 h-4" /> : <Home className="w-4 h-4" />}
                    <span className="font-medium">Rent</span>
                  </div>
                </div>

                <div 
                  onClick={() => houseSelectedId && handleCategorySelect('expense', !isUtilitySelected)}
                  className={getButtonClasses(isUtilitySelected)}
                >
                  <div className="flex items-center space-x-2">
                    {isUtilitySelected ? <Check className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                    <span className="font-medium">Utility</span>
                  </div>
                </div>
              </div>
            </div>

            {/*Utility Dropdown (Conditional) */}
            {isUtilitySelected && (
              <div className="space-y-1 animate-fadeIn">
                <p className="flex items-center text-sm font-medium text-gray-700">
                  <Zap className="w-4 h-4 mr-2 text-indigo-600" /> Select Utility Type
                </p>
                <select
                  value={selectedUtility}
                  onChange={(e) => setSelectedUtility(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All Utilities</option>
                  {selectedUtilities.map((utility) => (
                    <option key={utility.id} value={utility.id}>{utility.title}</option>
                  ))}
                </select>
              </div>
            )}

            {/*  Invoice List (Multi-select) */}
            <div className="space-y-2">
              <p className="flex items-center text-sm font-medium text-gray-700">
                <FileText className="w-4 h-4 mr-2 text-indigo-600" /> Select Invoices
              </p>
              <div className="border border-gray-300 rounded-lg overflow-hidden max-h-48 overflow-y-auto bg-white">
                {invoices.length > 0 ? (
                  invoices.map((invoice) => {
                    const isChecked = selectedInvoices.includes(invoice.id);
                    return (
                      <div 
                        key={invoice.id}
                        onClick={() => handleInvoiceToggle(invoice)}
                        className={`flex items-center justify-between p-3 border-b last:border-0 cursor-pointer hover:bg-gray-50 transition-colors ${isChecked ? 'bg-blue-50' : ''}`}
                      >
                        <div className="flex items-center">
                            <div className={`w-4 h-4 rounded border mr-3 flex items-center justify-center ${isChecked ? 'bg-blue-600 border-blue-600' : 'border-gray-400'}`}>
                              {isChecked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800 capitalize">
                                {invoice.month} {invoice.year} <span className="text-xs text-gray-500">({invoice.reference})</span>
                              </p>
                              <p className="text-xs text-gray-500">Status: {invoice.status}</p>
                            </div>
                        </div>
                        <span className="text-sm font-bold text-gray-700">
                          KES {parseInt(invoice.amount).toLocaleString()}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No unpaid invoices found for selected category.
                  </div>
                )}
              </div>
            </div>

            {/* Phone Number & Payment Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="flex items-center text-sm font-medium text-gray-700">
                  <Phone className="w-4 h-4 mr-2 text-indigo-600" /> Phone Number
                </p>
                <input
                  type="text"
                  name="phone_number"
                  placeholder="254XXXXXXXXX"
                  value={formDataPayment.phone_number}
                  onChange={handleChangePayment}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {formDataPayment.errorMessage && (
                  <p className="text-xs text-red-600">{formDataPayment.errorMessage}</p>
                )}
              </div>

              <div className="space-y-1">
                <p className="flex items-center text-sm font-medium text-gray-700">
                  <Calendar className="w-4 h-4 mr-2 text-indigo-600" /> Payment Date
                </p>
                <input
                  type="date"
                  name="paid_date"
                  value={formDataPayment.paid_date}
                  onChange={handleChangePayment}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Amount Input */}
            <div className="space-y-1">
              <p className="flex items-center text-sm font-medium text-gray-700">
                <DollarSign className="w-4 h-4 mr-2 text-indigo-600" /> Amount
              </p>
              <input
                type="number"
                name="amount"
                placeholder="Enter amount"
                value={formDataPayment.amount}
                onChange={handleChangePayment}
                required
                max={selectedRecord ? selectedRecord.overflow_amount : undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              {amountError && (
                <p className="text-xs text-red-600">{amountError}</p>
              )}
              {selectedRecord && (
                <p className="text-xs text-gray-500 text-right mt-1">
                  Max allowed: KES {parseFloat(selectedRecord.overflow_amount).toLocaleString('en-US')}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="pt-4 flex justify-end space-x-3 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCloseModalPayment}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedInvoices.length === 0}
                className={`px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 transition-colors flex items-center ${selectedInvoices.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Check className="w-4 h-4 mr-2" />
                Save Payment
              </button>
            </div>

          </form>
        </div>
      </div>
    </Modal>
  );
}

export default ReconcileOverPaymentModal;
